/**
 * Graph v2 — Entity color categories
 *
 * 8 color categories group ~30 entity types. Each category has a dedicated
 * color ramp with explicit light/dark values for node fill, stroke, and edge.
 * Icons are always white (#FFFFFF) on the category-colored fill.
 *
 * This is the single source of truth for graph node/edge coloring.
 */

// ── Types ──────────────────────────────────────────────────────────────────

export type Category =
  | 'identity'
  | 'compute'
  | 'network'
  | 'data'
  | 'findings'
  | 'application'
  | 'context'
  | 'framework';

export interface CategoryColorSet {
  fill: { light: string; dark: string };
  stroke: { light: string; dark: string };
  edge: { light: string; dark: string };
}

// ── Entity type → category mapping ─────────────────────────────────────────

export const CATEGORY_MAP: Record<string, Category> = {
  // Identity (Purple)
  user: 'identity',
  iam_role: 'identity',
  group: 'identity',
  certificate: 'identity',
  secret: 'identity', // nosemgrep: cade-no-hardcoded-secrets
  service_account: 'identity',
  api_key_token: 'identity',

  // Compute (Coral)
  host: 'compute',
  ec2: 'compute',
  lambda: 'compute',
  process: 'compute',
  container: 'compute',
  k8s_pod: 'compute',
  k8s_node: 'compute',
  eks_cluster: 'compute',
  aks_cluster: 'compute',
  gke_cluster: 'compute',
  auto_scaling_group: 'compute',

  // Network (Blue)
  ip: 'network',
  domain: 'network',
  url: 'network',
  network_zone: 'network',
  asn: 'network',
  internet: 'network',
  security_group: 'network',

  // Data (Teal)
  file: 'data',
  s3_bucket: 'data',
  storage_bucket: 'data',
  database: 'data',
  rds: 'data',
  dynamodb: 'data',
  data_finding: 'data',
  data_store: 'data',
  data_classification: 'data',
  data_flow: 'data',

  // Findings (Red)
  alert: 'findings',
  vulnerability: 'findings',
  incident: 'findings',
  threat_actor: 'findings',
  campaign: 'findings',
  mitre_technique: 'findings',
  indicator_ioc: 'findings',

  // Application (Amber)
  application: 'application',
  email: 'application',
  cloud_resource: 'application',
  api_endpoint: 'application',
  container_workload: 'application',

  // Context (Green)
  organization: 'context',
  geolocation: 'context',

  // Framework (Gray) — legacy, retained for backward compatibility
  policy: 'framework',

  // Decisions (mapped to application category for rendering)
  annotation: 'application',
  investigation_outcome: 'application',
  remediation_action: 'application',
  exception_exemption: 'application',
  fp_determination: 'application',
};

// ── Category color palette ─────────────────────────────────────────────────
// Light fill = lightest stop, Dark fill = darkest stop
// Light stroke = darkest stop, Dark stroke = lightest stop (contrast inversion)

export const CATEGORY_COLORS: Record<Category, CategoryColorSet> = {
  identity: {
    fill:   { light: '#D8D5FD', dark: '#3C3489' },
    stroke: { light: '#534AB7', dark: '#AFA9EC' },
    edge:   { light: '#7F77DD', dark: '#AFA9EC' },
  },
  compute: {
    fill:   { light: '#F5D5C9', dark: '#712B13' },
    stroke: { light: '#993C1D', dark: '#F0997B' },
    edge:   { light: '#D85A30', dark: '#F0997B' },
  },
  network: {
    fill:   { light: '#C9DFF5', dark: '#0C447C' },
    stroke: { light: '#185FA5', dark: '#85B7EB' },
    edge:   { light: '#378ADD', dark: '#85B7EB' },
  },
  data: {
    fill:   { light: '#C2EAD9', dark: '#085041' },
    stroke: { light: '#0F6E56', dark: '#5DCAA5' },
    edge:   { light: '#1D9E75', dark: '#5DCAA5' },
  },
  findings: {
    fill:   { light: '#F9D4D4', dark: '#791F1F' },
    stroke: { light: '#A32D2D', dark: '#F09595' },
    edge:   { light: '#E24B4A', dark: '#F09595' },
  },
  application: {
    fill:   { light: '#F5DCB0', dark: '#633806' },
    stroke: { light: '#854F0B', dark: '#EF9F27' },
    edge:   { light: '#BA7517', dark: '#EF9F27' },
  },
  context: {
    fill:   { light: '#D2E6B8', dark: '#27500A' },
    stroke: { light: '#3B6D11', dark: '#97C459' },
    edge:   { light: '#639922', dark: '#97C459' },
  },
  framework: {
    fill:   { light: '#E3E0D5', dark: '#444441' },
    stroke: { light: '#5F5E5A', dark: '#B4B2A9' },
    edge:   { light: '#888780', dark: '#B4B2A9' },
  },
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Returns the category for a given entity type, defaulting to 'framework'. */
export function getCategoryForType(type: string | undefined | null): Category {
  if (!type) return 'framework';
  return CATEGORY_MAP[type] ?? 'framework';
}

/** Returns { fill, stroke } hex colors for a node based on its type and theme. */
export function getNodeColors(
  type: string,
  isDark: boolean,
): { fill: string; stroke: string } {
  const cat = getCategoryForType(type);
  const colors = CATEGORY_COLORS[cat];
  return {
    fill: isDark ? colors.fill.dark : colors.fill.light,
    stroke: isDark ? colors.stroke.dark : colors.stroke.light,
  };
}

/** Returns the edge color for edges connected to a node of the given type. */
export function getEdgeColor(type: string, isDark: boolean): string {
  const cat = getCategoryForType(type);
  const colors = CATEGORY_COLORS[cat];
  return isDark ? colors.edge.dark : colors.edge.light;
}

/**
 * 6-layer ontology mapping — used for layer toggle filtering and grouping.
 * Layer colors are distinct from category colors: they apply only to layer UI
 * indicators (toggle buttons, band labels), not to entity node rendering.
 */
export type GraphLayer = 'infrastructure' | 'identity' | 'application' | 'data' | 'threat' | 'decisions';

export const LAYER_COLORS: Record<GraphLayer, string> = {
  infrastructure: '#5AAEF5',
  identity: '#4EDBA8',
  application: '#A89FF0',
  data: '#F08050',
  threat: '#F07070',
  decisions: '#E8A840',
};

/** Returns the 6-layer assignment for a given entity type. */
export function getLayerForType(type: string | undefined | null): GraphLayer {
  if (!type) return 'infrastructure';
  // Import from ontology seed would create circular dep — inline the mapping
  const layerMap: Record<string, GraphLayer> = {
    host: 'infrastructure', ec2: 'infrastructure', lambda: 'infrastructure', process: 'infrastructure',
    container: 'infrastructure', k8s_pod: 'infrastructure', k8s_node: 'infrastructure',
    eks_cluster: 'infrastructure', aks_cluster: 'infrastructure', gke_cluster: 'infrastructure',
    auto_scaling_group: 'infrastructure', ip: 'infrastructure', domain: 'infrastructure',
    url: 'infrastructure', network_zone: 'infrastructure', asn: 'infrastructure',
    internet: 'infrastructure', security_group: 'infrastructure', certificate: 'infrastructure',
    email: 'infrastructure', organization: 'infrastructure', geolocation: 'infrastructure',
    user: 'identity', service_account: 'identity', iam_role: 'identity', group: 'identity',
    secret: 'identity', api_key_token: 'identity',
    application: 'application', cloud_resource: 'application', api_endpoint: 'application',
    container_workload: 'application',
    file: 'data', s3_bucket: 'data', storage_bucket: 'data', database: 'data', rds: 'data',
    dynamodb: 'data', data_finding: 'data', data_store: 'data', data_classification: 'data',
    data_flow: 'data',
    alert: 'threat', vulnerability: 'threat', incident: 'threat', threat_actor: 'threat',
    campaign: 'threat', mitre_technique: 'threat', indicator_ioc: 'threat',
    annotation: 'decisions', investigation_outcome: 'decisions', remediation_action: 'decisions',
    exception_exemption: 'decisions', fp_determination: 'decisions',
  };
  return layerMap[type] ?? 'infrastructure';
}

/** Returns underlay glow params for critical/high severity, null otherwise. */
export function getSeverityGlow(
  severity: string | undefined | null,
): { color: string; opacity: number } | null {
  if (severity === 'critical') return { color: '#dc2626', opacity: 0.15 };
  if (severity === 'high') return { color: '#ea580c', opacity: 0.15 };
  return null;
}
